import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  speichereNotiz,
  loescheNotiz,
  ladeDateiHoch,
  ladeDateiHerunter,
  loescheDatei,
} from 'modules/wissen/features/wissenSlice';
import { rendereMarkdown, ordnerMitTiefe, formatiereGroesse } from 'modules/wissen/components/wissenHelpers';
import EditorToolbar from 'modules/wissen/components/EditorToolbar';

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB pro Anhang

/**
 * Vollbild-Editor fuer eine Notiz: Titel, Markdown-Inhalt mit Format-Leiste und
 * Vorschau, Tags, Ordnerzuordnung, Favorit und angehaengte Dateien. Speichert
 * verschluesselt ueber den Slice.
 */
export default function NotizEditor({ notiz, ordner, dateien, onClose }) {
  const dispatch = useDispatch();
  const textareaRef = useRef(null);
  const [notizUuid, setNotizUuid] = useState(notiz.uuid || null);
  const [titel, setTitel] = useState(notiz.titel || '');
  const [text, setText] = useState(notiz.text || '');
  const [tagsText, setTagsText] = useState((notiz.tags || []).join(', '));
  const [ordnerId, setOrdnerId] = useState(notiz.ordnerId ?? '');
  const [favorit, setFavorit] = useState(!!notiz.favorit);
  const [vorschau, setVorschau] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  const anhaenge = dateien.filter((d) => d.notizUuid && d.notizUuid === notizUuid);

  // Speichert die Notiz verschluesselt und liefert ihre uuid (auch fuer neue Notizen).
  const sichereNotiz = async () => {
    const tags = tagsText.split(',').map((t) => t.trim()).filter(Boolean);
    const gespeichert = await dispatch(
      speichereNotiz({
        uuid: notizUuid,
        titel: titel.trim() || 'Ohne Titel',
        text,
        tags,
        ordnerId: ordnerId === '' ? null : Number(ordnerId),
        favorit,
      })
    ).unwrap();
    setNotizUuid(gespeichert.uuid);
    return gespeichert.uuid;
  };

  const speichern = async () => {
    if (!titel.trim() && !text.trim() && !notizUuid) {
      onClose();
      return;
    }
    setLaeuft(true);
    await sichereNotiz();
    setLaeuft(false);
    onClose();
  };

  const loeschen = async () => {
    if (notizUuid && window.confirm('Diese Notiz und ihre Anhänge löschen?')) {
      await dispatch(loescheNotiz(notizUuid));
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-paper">
      {/* Kopfzeile */}
      <header className="flex items-center gap-2 border-b border-line bg-paper-soft px-4 py-3 sm:px-6">
        <button type="button" onClick={onClose} className="rounded-lg px-2 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink">
          ← Zurück
        </button>
        <input
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="Titel der Notiz…"
          className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1.5 font-serif text-xl font-semibold text-ink focus:border-line focus:bg-paper focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setFavorit((v) => !v)}
          title="Favorit"
          className={`rounded-lg px-2 py-1.5 text-lg ${favorit ? 'text-amber-500' : 'text-ink-muted hover:text-ink'}`}
        >
          {favorit ? '★' : '☆'}
        </button>
        {notizUuid && (
          <button type="button" onClick={loeschen} className="hidden rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 sm:block">
            Löschen
          </button>
        )}
        <button type="button" onClick={speichern} disabled={laeuft} className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50">
          {laeuft ? 'Speichere…' : 'Speichern'}
        </button>
      </header>

      {/* Inhalt */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-3">
          {/* Editor */}
          <div className="lg:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex gap-1 text-sm">
                <button type="button" onClick={() => setVorschau(false)} className={`rounded px-2 py-1 ${!vorschau ? 'bg-paper-sunken text-ink' : 'text-ink-muted'}`}>Bearbeiten</button>
                <button type="button" onClick={() => setVorschau(true)} className={`rounded px-2 py-1 ${vorschau ? 'bg-paper-sunken text-ink' : 'text-ink-muted'}`}>Vorschau</button>
              </div>
            </div>

            {vorschau ? (
              <div
                className="min-h-[60vh] rounded-lg border border-line bg-paper-soft px-5 py-4 text-ink"
                dangerouslySetInnerHTML={{ __html: rendereMarkdown(text) || '<p class="text-ink-muted">Nichts zu zeigen.</p>' }}
              />
            ) : (
              <div>
                <EditorToolbar textareaRef={textareaRef} value={text} onChange={setText} />
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Schreibe hier ausführlich dein Wissen, deine Ideen… (Markdown wird unterstützt)"
                  className="min-h-[60vh] w-full rounded-b-lg border border-line bg-paper px-4 py-3 font-mono text-sm leading-relaxed text-ink focus:border-brand-600 focus:outline-none"
                />
              </div>
            )}
          </div>

          {/* Meta-Spalte */}
          <aside className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">Ordner</label>
              <select
                value={ordnerId}
                onChange={(e) => setOrdnerId(e.target.value)}
                className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
              >
                <option value="">— Kein Ordner —</option>
                {ordnerMitTiefe(ordner).map((o) => (
                  <option key={o.uuid} value={o.id}>{' '.repeat(o.tiefe * 2)}{o.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">Tags</label>
              <input
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="idee, projekt, wichtig"
                className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
              />
              <p className="mt-1 text-xs text-ink-muted">Komma-getrennt</p>
            </div>

            <AnhangBereich anhaenge={anhaenge} sichereNotiz={sichereNotiz} />

            {notizUuid && (
              <button type="button" onClick={loeschen} className="w-full rounded-lg border border-line px-3 py-2 text-sm text-red-600 hover:bg-red-50 sm:hidden">
                Notiz löschen
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

/** Liste der angehaengten Dateien mit Upload/Download/Loeschen. */
function AnhangBereich({ anhaenge, sichereNotiz }) {
  const dispatch = useDispatch();
  const eingabe = useRef(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  const anhaengen = async (event) => {
    const dateiListe = Array.from(event.target.files || []);
    event.target.value = '';
    if (dateiListe.length === 0) return;
    setFehler(null);
    setLaedt(true);
    try {
      // Neue Notiz vor dem ersten Anhang speichern, um die uuid zu erhalten.
      const uuid = await sichereNotiz();
      for (const file of dateiListe) {
        if (file.size > MAX_BYTES) {
          setFehler(`„${file.name}" ist größer als ${formatiereGroesse(MAX_BYTES)} und wurde übersprungen.`);
          continue;
        }
        await dispatch(ladeDateiHoch({ file, notizUuid: uuid })).unwrap();
      }
    } catch (ex) {
      setFehler('Anhängen fehlgeschlagen.');
    }
    setLaedt(false);
  };

  const loeschen = (d) => {
    if (window.confirm(`Anhang „${d.name}" löschen?`)) dispatch(loescheDatei(d.uuid));
  };

  return (
    <div className="rounded-lg border border-line bg-paper-soft p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">📎 Anhänge</span>
        <button
          type="button"
          onClick={() => eingabe.current?.click()}
          disabled={laedt}
          className="rounded-lg px-2 py-1 text-sm font-medium text-brand-600 hover:bg-brand-600/10 disabled:opacity-50"
        >
          {laedt ? 'Lade…' : '+ Datei'}
        </button>
        <input ref={eingabe} type="file" multiple onChange={anhaengen} className="hidden" />
      </div>

      {fehler && <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700">{fehler}</p>}

      {anhaenge.length === 0 ? (
        <p className="text-xs text-ink-muted">Keine Anhänge · max. {formatiereGroesse(MAX_BYTES)} pro Datei, verschlüsselt.</p>
      ) : (
        <ul className="divide-y divide-line">
          {anhaenge.map((d) => (
            <li key={d.uuid} className="flex items-center gap-2 py-2">
              <span aria-hidden="true">📄</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">{d.name}</p>
                <p className="text-xs text-ink-muted">{formatiereGroesse(d.groesseOriginal)}</p>
              </div>
              <button type="button" onClick={() => dispatch(ladeDateiHerunter(d))} className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-600/10" title="Herunterladen">↓</button>
              <button type="button" onClick={() => loeschen(d)} className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" title="Löschen">✕</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
